import type {
  Address,
  GetTripResponse,
} from '../../services/service-trips-api/types/trips';

export type TripsListSearchQueryParams = {
  searchItem: string;
  address: string;
  fromDate?: string;
  toDate?: string;
  endDate?: string;
  grouping: string;
  pageSize: string;
  pageNum: string;
};

const ADDRESS_POSITION: Record<string, number> = {
  pickup: 0,
  destination: 1,
};

export const getAddressType = (
  addresses: Address[] | undefined,
  type: string
) => {
  if (!addresses) return undefined;
  const byType = addresses.find((address) => address.type === type);
  if (byType) return byType;
  // Fall back to positional when type field is not set on addresses
  // type is a small controlled set of known address type strings, not user input
  // eslint-disable-next-line security/detect-object-injection
  const index = ADDRESS_POSITION[type];
  // index is a fixed positional constant (0 or 1) from ADDRESS_POSITION above
  // eslint-disable-next-line security/detect-object-injection
  return index !== undefined ? addresses[index] : undefined;
};

const buildAddressFromParts = (address: Address): string | undefined => {
  const street = [address.streetName, address.houseNo, address.houseLetter]
    .filter((v) => v != null && v !== '')
    .join(' ');
  const location = [address.zipCode, address.city].filter(Boolean).join(' ');
  return [street, location].filter(Boolean).join(', ') || undefined;
};

export const getAddress = (
  addresses: Address[] | undefined,
  type: string
): string | undefined => {
  const address = getAddressType(addresses, type);
  if (!address) return undefined;
  return address.fullAddress ?? buildAddressFromParts(address);
};

export const getShortAddress = (
  addresses: Address[] | undefined,
  type: string
) => {
  const address = getAddressType(addresses, type);

  if (address) {
    const maybeAddress =
      address.poiname ||
      address.pOIName ||
      [address.streetName, address.houseNo].filter((v) => v != null && v !== '').join(' ');
    if (!maybeAddress?.length) {
      return address.fullAddress;
    }
    return maybeAddress;
  }
  return undefined;
};

export const isDateDefined = (value: string | null | undefined): value is string => {
  return !!value && value !== '0';
};

export function getAddressUniqueId(
  address: Pick<Address, 'pOIId' | 'streetID'>
) {
  return `${address.pOIId}:${address.streetID}`;
}

type TripWithPickup = Pick<GetTripResponse, 'id' | 'pickUpDateTime'>;

export type TripGroup<TTrip extends TripWithPickup = GetTripResponse> = {
  coordinatedBookRefNr: number;
  items: ReadonlyArray<TTrip>;
};

export const getTripPickupTimestamp = (
  trip: Pick<GetTripResponse, 'pickUpDateTime'>
) => {
  if (!isDateDefined(trip.pickUpDateTime)) return Number.POSITIVE_INFINITY;

  const timestamp = Date.parse(trip.pickUpDateTime);
  return Number.isNaN(timestamp) ? Number.POSITIVE_INFINITY : timestamp;
};

export const sortTripsByPickupTime = <TTrip extends TripWithPickup>(
  trips: ReadonlyArray<TTrip>
) =>
  trips
    .map((trip) => ({
      trip,
      pickupTimestamp: getTripPickupTimestamp(trip),
    }))
    .sort((a, b) => {
      if (a.pickupTimestamp < b.pickupTimestamp) return -1;
      if (a.pickupTimestamp > b.pickupTimestamp) return 1;
      return a.trip.id - b.trip.id;
    })
    .map(({ trip }) => trip);

const getTripGroupPickupTimestamp = <TTrip extends TripWithPickup>({
  items,
}: TripGroup<TTrip>) =>
  items.reduce(
    (earliest, item) => Math.min(earliest, getTripPickupTimestamp(item)),
    Number.POSITIVE_INFINITY
  );

export const sortTripGroupsByPickupTime = <TTrip extends TripWithPickup>(
  groups: ReadonlyArray<TripGroup<TTrip>>
) =>
  groups
    .map((group) => ({
      group: {
        ...group,
        items: sortTripsByPickupTime(group.items),
      },
    }))
    .map(({ group }) => ({
      group,
      pickupTimestamp: getTripGroupPickupTimestamp(group),
    }))
    .sort((a, b) => {
      if (a.pickupTimestamp < b.pickupTimestamp) return -1;
      if (a.pickupTimestamp > b.pickupTimestamp) return 1;
      return a.group.coordinatedBookRefNr - b.group.coordinatedBookRefNr;
    })
    .map(({ group }) => group);
